/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useSlotId } from '@react-aria/utils';
import { clsx } from 'clsx';
import {
    type AriaAttributes,
    Children,
    type ComponentPropsWithoutRef,
    cloneElement,
    createContext,
    type ForwardedRef,
    isValidElement,
    memo,
    type ReactNode,
    type RefCallback,
    type TransitionEvent,
    useContext,
    useId,
    useLayoutEffect,
    useState,
} from 'react';
import type { PressEvent, PressHookProps } from 'react-aria';
import {
    type ContextValue,
    DisclosureGroupStateContext,
    DisclosureStateContext,
    Provider,
    useContextProps,
} from 'react-aria-components';
import {
    type DisclosureState,
    type DisclosureProps as StatelyDisclosureProps,
    useDisclosureState,
} from 'react-stately';

import { Icon, type SVGIconProps } from '@gitbook/spine-icons';
import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

export interface DisclosureProps extends StatelyDisclosureProps {
    children: ReactNode | ((renderProps: DisclosureState) => ReactNode);
    id?: string;
}

export {
    DisclosureGroup,
    type DisclosureGroupProps,
    DisclosureStateContext,
} from 'react-aria-components';

export function Disclosure(props: DisclosureProps) {
    const groupState = useContext(DisclosureGroupStateContext);
    const defaultId = useId();
    const id = props.id || defaultId;
    const isExpanded = groupState ? groupState.expandedKeys.has(id) : props.isExpanded;
    const state = useDisclosureState({
        ...props,
        isExpanded,
        onExpandedChange: (isExpanded) => {
            groupState?.toggleKey(id);
            props.onExpandedChange?.(isExpanded);
        },
    });
    const panelId = useSlotId();
    return (
        <DisclosureStateContext.Provider value={state}>
            <Provider
                values={[
                    [DisclosureStateContext, state],
                    [DisclosureTriggerContext, { 'aria-controls': panelId }],
                    [DisclosurePanelContext, { id: panelId }],
                ]}
            >
                {typeof props.children === 'function' ? props.children(state) : props.children}
            </Provider>
        </DisclosureStateContext.Provider>
    );
}

export interface DisclosureTriggerProps {
    /**
     * The children to render. Must be a single React element.
     */
    children?: ReactNode;
    /**
     * The value of the `aria-haspopup` attribute.
     */
    'aria-haspopup'?: AriaAttributes['aria-haspopup'];

    /**
     * The value of the `aria-controls` attribute.
     */
    'aria-controls'?: AriaAttributes['aria-controls'];
}

const DisclosureTriggerContext = createContext<ContextValue<DisclosureTriggerProps, HTMLElement>>(
    {}
);

export function DisclosureTrigger({
    ref,
    ...props
}: DisclosureTriggerProps & { ref?: ForwardedRef<HTMLElement> }) {
    [props, ref] = useContextProps(props, ref, DisclosureTriggerContext);
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosureTrigger must be used within a Disclosure');

    const child = Children.only(props.children);
    assert(
        isValidElement<
            { onPress: PressHookProps['onPress'] } & AriaAttributes & {
                    id: string;
                    ref: RefCallback<HTMLElement | null>;
                }
        >(child),
        'DisclosureTrigger must have a single React element as its child.'
    );

    const mergedRef = useMergedRefs<HTMLElement>(ref, child.props.ref);

    return cloneElement(child, {
        ref: mergedRef,
        onPress: (event: PressEvent) => {
            child.props.onPress?.(event);
            state.toggle();
        },
        'aria-expanded': state.isExpanded,
        'aria-controls': props['aria-controls'],
    });
}

type DisclosureAnimateProp = 'size' | 'opacity';

export interface DisclosurePanelProps extends ComponentPropsWithoutRef<'div'> {
    /**
     * The content rendered inside the panel.
     */
    children?: ReactNode;

    /**
     * The orientation of the panel.
     */
    orientation?: 'vertical' | 'horizontal';

    /**
     * The properties to animate.
     */
    animateProps?: DisclosureAnimateProp[];

    /**
     * The class name applied to the content wrapper.
     */
    contentClassName?: string;
}

const DisclosurePanelContext = createContext<ContextValue<DisclosurePanelProps, HTMLDivElement>>(
    {}
);

export function DisclosurePanel({
    ref,
    ...props
}: DisclosurePanelProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, DisclosurePanelContext);
    const {
        animateProps = ['size', 'opacity'],
        children,
        className,
        contentClassName,
        onTransitionEnd,
        orientation = 'vertical',
        ...panelProps
    } = props;
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosurePanel must be used within a Disclosure');
    const [animationState, setAnimationState] = useState<
        'entering' | 'entered' | 'exiting' | 'exited'
    >(state.isExpanded ? 'entered' : 'exited');
    useLayoutEffect(() => {
        setAnimationState(state.isExpanded ? 'entering' : 'exiting');
    }, [state.isExpanded]);

    const animateSize = animateProps.includes('size');
    const animateOpacity = animateProps.includes('opacity');
    const isContentVisible = animationState !== 'exited';
    // Only clip while the size is animating. Once the panel is fully open, switch to
    // `overflow-visible` so content that legitimately overflows the box (e.g. focus rings,
    // shadows, popovers) isn't clipped.
    const isClipping = animationState !== 'entered';
    const sizeClassName =
        orientation === 'vertical'
            ? state.isExpanded || !animateSize
                ? 'grid-rows-[1fr]'
                : 'grid-rows-[0fr]'
            : state.isExpanded || !animateSize
              ? 'grid-cols-[1fr]'
              : 'grid-cols-[0fr]';

    return (
        <div
            ref={ref}
            {...panelProps}
            className={clsx(
                'grid transition-[grid-template-rows,grid-template-columns,opacity] motion-reduce:transition-none',
                isClipping ? 'overflow-clip' : 'overflow-visible',
                'duration-75 ease-out',
                sizeClassName,
                animateOpacity && (state.isExpanded ? 'opacity-100' : 'opacity-0'),
                className
            )}
            aria-hidden={!state.isExpanded}
            style={{
                contentVisibility: animationState === 'exited' ? 'hidden' : 'visible',
                ...props.style,
            }}
            onTransitionEnd={(event: TransitionEvent<HTMLDivElement>) => {
                onTransitionEnd?.(event);

                if (event.target !== event.currentTarget) {
                    return;
                }

                if (!state.isExpanded && animationState === 'exiting') {
                    setAnimationState('exited');
                } else if (state.isExpanded && animationState === 'entering') {
                    setAnimationState('entered');
                }
            }}
        >
            <div
                className={clsx(
                    isClipping ? 'overflow-clip' : 'overflow-visible',
                    orientation === 'vertical' ? 'min-h-0' : 'min-w-0',
                    !isContentVisible && 'invisible',
                    contentClassName
                )}
            >
                {children}
            </div>
        </div>
    );
}

export interface DisclosureChevronProps extends SVGIconProps {}

export const DisclosureChevron = memo(function DisclosureChevron(props: DisclosureChevronProps) {
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosureChevron must be used within a Disclosure');
    return (
        <Icon.ChevronRight
            {...props}
            className={clsx(
                'shrink-0 transition',
                state.isExpanded && 'rotate-90',
                props.className
            )}
        />
    );
});
